import * as React from 'react'
import { useFieldArray, useForm, useFormState, type UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ExternalLink, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ProductSearchPicker } from '@/components/po/ProductSearchPicker'
import { useEventLocations } from '@/hooks/useEvents'
import {
  deleteRequestPhoto,
  fetchRequestPhotoUrl,
  searchCustomers,
  uploadRequestPhoto,
  useChangeRequestStatus,
  useCreateRequestBatch,
  useUpdateRequest,
  ORIGIN_LABELS,
  REQUEST_STATUS_LABELS,
  type BatchProductEntry,
  type ProductRequest,
  type ProductRequestBatchPayload,
  type ProductRequestUpdatePayload,
  type RequestOrigin,
  type RequestStatus,
  type ShopifyCustomerInfo,
} from '@/hooks/useProductRequests'
import { fmtDate } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { PhotoLightbox } from './PhotoLightbox'

// Same three names RequestFilters.tsx offers — display names, not usernames;
// the backend round-trips the string as-is.
const ASSIGNEES = ['Gina', 'Rachel', 'Olivia']

const ORIGINS: RequestOrigin[] = ['BREWSTER', 'PHONE_EMAIL', 'EVENT']

const STATUSES: RequestStatus[] = [
  'OPEN',
  'NEED_TO_SOURCE',
  'SOURCED',
  'ADDED_TO_PO',
  'ORDERED',
  'CUSTOMER_NOTIFIED',
  'PURCHASED',
  'CLOSED_NO_PURCHASE',
]

const BUYING_DECISIONS: { value: string; label: string }[] = [
  { value: 'TBD', label: 'TBD' },
  { value: 'WILL_CARRY', label: 'Will Carry' },
  { value: 'WILL_NOT_CARRY', label: 'Will Not Carry' },
]

// A staff phone snap; the backend rejects anything larger, so catch it here
// rather than after a 10 MB upload round-trip.
const PHOTO_MAX_BYTES = 10 * 1024 * 1024

const selectCls =
  'h-8 w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand)]'
const inputCls = 'h-8 text-xs'

/**
 * The first product entry keeps the un-suffixed test ids the single-product
 * form always had, so nothing that targets "the product fields" has to know
 * the form became an array.
 */
function tid(base: string, index: number): string {
  return index === 0 ? base : `${base}-${index}`
}

// Per-product fields. Everything else on the form (customer, context,
// assignment, notes, status) is shared by every entry in the batch.
const entrySchema = z.object({
  // The toggle between the Shopify picker and the typed block.
  manual_mode: z.boolean(),
  variant_id: z.string(),
  product_id: z.string(),
  product_title: z.string(),
  variant_title: z.string(),
  sku: z.string(),
  on_hand: z.string(),
  brand: z.string(),
  product_type: z.string(),
  size: z.string(),
  color: z.string(),
  material: z.string(),
  approx_price: z.string(),
  reference_url: z.string(),
  qty: z.string(),
  vendor: z.string(),
  est_cost: z.string(),
  buying_decision: z.enum(['', 'TBD', 'WILL_CARRY', 'WILL_NOT_CARRY']),
})

type ProductEntryValues = z.infer<typeof entrySchema>

const requestSchema = z
  .object({
    customer_name: z.string().trim().min(1, 'Customer name is required'),
    customer_email: z.string(),
    customer_phone: z.string(),
    shopify_customer_id: z.string(),
    shopify_customer_url: z.string(),
    products: z.array(entrySchema).min(1),
    needed_by: z.string(),
    originated_from: z.enum(['', 'BREWSTER', 'PHONE_EMAIL', 'EVENT']),
    event_location_id: z.string(),
    event_location_name: z.string(),
    urgency: z.enum(['STANDARD', 'URGENT']),
    assigned_to: z.string(),
    follow_up_date: z.string(),
    notes: z.string(),
    status: z.enum([
      'OPEN',
      'NEED_TO_SOURCE',
      'SOURCED',
      'ADDED_TO_PO',
      'ORDERED',
      'CUSTOMER_NOTIFIED',
      'PURCHASED',
      'CLOSED_NO_PURCHASE',
    ]),
  })
  // Every rule lives in one refinement (rather than one per entry schema) so a
  // half-filled product never hides the context errors underneath it.
  .superRefine((v, ctx) => {
    v.products.forEach((p, i) => {
      // Mirrors the backend's _title_required_without_variant: a request is
      // identified either by a Shopify variant or by a typed product name.
      if (p.manual_mode) {
        if (!p.product_title.trim()) {
          ctx.addIssue({
            code: 'custom',
            path: ['products', i, 'product_title'],
            message: 'Describe the product the customer asked for',
          })
        }
      } else if (!p.variant_id) {
        ctx.addIssue({
          code: 'custom',
          path: ['products', i, 'product_title'],
          message: 'Pick a product, or switch to entering it manually',
        })
      }
      const qty = Number(p.qty)
      if (!Number.isInteger(qty) || qty < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['products', i, 'qty'],
          message: 'Qty must be 1 or more',
        })
      }
    })
    if (!v.originated_from) {
      ctx.addIssue({
        code: 'custom',
        path: ['originated_from'],
        message: 'Where did this request come from?',
      })
    }
    if (v.originated_from === 'EVENT' && !v.event_location_id) {
      ctx.addIssue({
        code: 'custom',
        path: ['event_location_id'],
        message: 'Pick the event',
      })
    }
  })

type LogRequestValues = z.infer<typeof requestSchema>

function localDateIso(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const EMPTY_ENTRY: ProductEntryValues = {
  manual_mode: false,
  variant_id: '',
  product_id: '',
  product_title: '',
  variant_title: '',
  sku: '',
  on_hand: '',
  brand: '',
  product_type: '',
  size: '',
  color: '',
  material: '',
  approx_price: '',
  reference_url: '',
  qty: '1',
  vendor: '',
  est_cost: '',
  buying_decision: '',
}

const EMPTY: LogRequestValues = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  shopify_customer_id: '',
  shopify_customer_url: '',
  products: [{ ...EMPTY_ENTRY }],
  needed_by: '',
  originated_from: '',
  event_location_id: '',
  event_location_name: '',
  urgency: 'STANDARD',
  assigned_to: '',
  follow_up_date: '',
  notes: '',
  status: 'OPEN',
}

export interface LogRequestModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set, the modal edits this request instead of creating one. */
  editRequest?: ProductRequest | null
  /** Create-mode prefills — the Events pages hand over the booth context. */
  presets?: Partial<{
    customer_name: string
    originated_from: RequestOrigin
    event_location_id: number
    event_location_name: string
  }>
}

/** A staged reference photo, one slot per product entry. */
interface PhotoSlot {
  file: File | null
  /** Object URL for the preview — this component owns revoking it. */
  url: string | null
  remove: boolean
}

const EMPTY_SLOT: PhotoSlot = { file: null, url: null, remove: false }

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 border-b border-[var(--line-warm)] pb-1 text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
      {children}
    </div>
  )
}

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n)
}

/** Dropdown rows are one line — spend is a scale cue, not an invoice. */
function fmtMoneyCompact(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

const CUSTOMER_SEARCH_MIN_CHARS = 2
const CUSTOMER_SEARCH_DEBOUNCE_MS = 250

/** The subset of react-hook-form's field object this input needs. */
interface CustomerTextField {
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur: () => void
  ref: React.Ref<HTMLInputElement>
}

/**
 * One of the three customer fields, with the Shopify typeahead under it.
 * All three search the same endpoint, so staff can start from whichever
 * detail the customer actually gave them — a name at the counter, an address
 * off an email, a number off a voicemail — instead of having to produce the
 * email the old exact-match lookup demanded.
 */
function CustomerSearchField({
  field,
  testid,
  type,
  disabled,
  autoFocus,
  onEdit,
  onPick,
  // <FormControl> clones this element with id / aria-describedby / aria-invalid;
  // they belong on the input, not on the positioning wrapper.
  ...aria
}: {
  field: CustomerTextField
  testid: string
  type?: string
  disabled: boolean
  autoFocus?: boolean
  /** The user typed here — any earlier Shopify pick no longer describes them. */
  onEdit: () => void
  onPick: (customer: ShopifyCustomerInfo) => void
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [results, setResults] = React.useState<ShopifyCustomerInfo[]>([])
  const [highlight, setHighlight] = React.useState(0)
  const value = field.value

  // Only the field being typed in searches: a pick fills the other two
  // programmatically, and they must not fire their own lookups off it.
  React.useEffect(() => {
    if (!open) return
    const q = value.trim()
    if (q.length < CUSTOMER_SEARCH_MIN_CHARS) {
      setResults([])
      return
    }
    let cancelled = false
    const t = setTimeout(() => {
      searchCustomers(q)
        .then((rows) => {
          if (cancelled) return
          setResults(rows)
          setHighlight(0)
        })
        .catch(() => {
          // The typeahead is a convenience, never a gate.
          if (!cancelled) setResults([])
        })
    }, CUSTOMER_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [open, value])

  function pick(customer: ShopifyCustomerInfo) {
    setOpen(false)
    setResults([])
    onPick(customer)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape' && open) {
      // Close the list only — don't let the Dialog catch this and drop the
      // half-filled form.
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      return
    }
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => (h + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => (h - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      // While the list is open Enter picks a customer; it must not submit.
      e.preventDefault()
      pick(results[highlight] ?? results[0])
    }
  }

  return (
    <div className="relative">
      <Input
        {...aria}
        {...field}
        type={type}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        className={inputCls}
        data-testid={testid}
        onChange={(e) => {
          field.onChange(e)
          onEdit()
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          field.onBlur()
          setOpen(false)
        }}
        onKeyDown={handleKeyDown}
      />
      {open && results.length > 0 && (
        <div
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto border border-[var(--line-warm)] bg-[var(--surface-card)] shadow-md"
          data-testid={`${testid}-results`}
        >
          {results.map((c, i) => (
            <button
              key={c.shopify_customer_id}
              type="button"
              // mousedown fires before blur — swallowing it keeps the focus on
              // the input so this row's click actually lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(c)}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                'flex w-full items-center gap-2 border-b border-[var(--line-warm)] px-2 py-1.5 text-left text-xs last:border-b-0',
                i === highlight && 'bg-[var(--surface-header)]'
              )}
            >
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium text-[var(--text-primary)]">
                  {c.display_name || c.email}
                </span>
                {c.email && (
                  <span className="ml-1.5 text-[var(--mute)]">{c.email}</span>
                )}
              </span>
              <span className="shrink-0 tabular-nums text-[var(--mute)]">
                {c.orders_count} {c.orders_count === 1 ? 'order' : 'orders'} ·{' '}
                {fmtMoneyCompact(c.total_spent)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Fields the backend can accept on both POST and PATCH. Empty strings are sent
// (they clear a text column); dates and numbers are omitted when blank, since
// the backend validates the 'YYYY-MM-DD' pattern and reads None as "unchanged".
function buildShared(v: LogRequestValues): Partial<ProductRequestUpdatePayload> {
  const out: Partial<ProductRequestUpdatePayload> = {
    customer_name: v.customer_name.trim(),
    customer_email: v.customer_email.trim(),
    customer_phone: v.customer_phone.trim(),
    shopify_customer_id: v.shopify_customer_id,
    shopify_customer_url: v.shopify_customer_url,
    originated_from: v.originated_from as RequestOrigin,
    urgency: v.urgency,
    assigned_to: v.assigned_to,
    notes: v.notes.trim(),
  }

  if (v.needed_by) out.needed_by = v.needed_by
  if (v.follow_up_date) out.follow_up_date = v.follow_up_date

  if (v.originated_from === 'EVENT') {
    if (v.event_location_id) out.event_location_id = Number(v.event_location_id)
    out.event_location_name = v.event_location_name
  } else {
    out.event_location_name = ''
  }

  return out
}

/** The product half of the payload — one of these per batch entry. */
function buildProduct(e: ProductEntryValues): BatchProductEntry {
  const out: BatchProductEntry = {
    product_title: e.product_title.trim(),
    qty: Math.trunc(Number(e.qty)),
  }

  if (e.manual_mode) {
    out.brand = e.brand.trim()
    out.product_type = e.product_type.trim()
    out.size = e.size.trim()
    out.color = e.color.trim()
    out.material = e.material.trim()
    out.reference_url = e.reference_url.trim()
    if (e.approx_price.trim()) out.approx_price = Number(e.approx_price)
    out.vendor = e.vendor.trim()
    if (e.est_cost.trim()) out.est_cost = Number(e.est_cost)
    if (e.buying_decision) out.buying_decision = e.buying_decision
  } else {
    if (e.variant_id) out.variant_id = Number(e.variant_id)
    if (e.product_id) out.product_id = Number(e.product_id)
    out.variant_title = e.variant_title
    out.sku = e.sku
  }

  return out
}

/**
 * One product on the request: the Shopify picker or the typed block, its qty,
 * and (manual only) its own reference photo. Create mode renders several of
 * these; edit mode renders exactly one and hides the add/remove controls.
 */
function ProductEntryCard({
  form,
  index,
  busy,
  photo,
  onPhotoPick,
  onPhotoRemove,
  photoInputRef,
  onViewPhoto,
  onRemove,
}: {
  form: UseFormReturn<LogRequestValues>
  index: number
  busy: boolean
  photo: PhotoSlot
  onPhotoPick: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void
  onPhotoRemove: (index: number) => void
  photoInputRef: (el: HTMLInputElement | null) => void
  /** Only set for a photo that is already stored — a staged file has no
   *  request to fetch it back from. */
  onViewPhoto?: () => void
  onRemove?: () => void
}) {
  const { errors } = useFormState({ control: form.control })
  const manualMode = form.watch(`products.${index}.manual_mode`)
  const pickedTitle = form.watch(`products.${index}.product_title`)
  const pickedVariantId = form.watch(`products.${index}.variant_id`)
  const pickedVariantTitle = form.watch(`products.${index}.variant_title`)
  const pickedSku = form.watch(`products.${index}.sku`)
  const pickedOnHand = form.watch(`products.${index}.on_hand`)
  const titleError = errors.products?.[index]?.product_title

  function switchToManual() {
    form.setValue(`products.${index}.manual_mode`, true)
    form.setValue(`products.${index}.variant_id`, '')
    form.setValue(`products.${index}.product_id`, '')
    form.setValue(`products.${index}.variant_title`, '')
    form.setValue(`products.${index}.sku`, '')
    form.setValue(`products.${index}.on_hand`, '')
    form.clearErrors(`products.${index}.product_title`)
  }

  function switchToSearch() {
    form.setValue(`products.${index}.manual_mode`, false)
    form.clearErrors(`products.${index}.product_title`)
  }

  function clearPick() {
    form.setValue(`products.${index}.variant_id`, '')
    form.setValue(`products.${index}.product_id`, '')
    form.setValue(`products.${index}.product_title`, '')
    form.setValue(`products.${index}.variant_title`, '')
    form.setValue(`products.${index}.sku`, '')
    form.setValue(`products.${index}.on_hand`, '')
  }

  return (
    <div className="flex flex-col gap-3 border border-[var(--line-warm)] p-3">
      {onRemove && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[var(--text-primary)]">
            Product {index + 1}
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            aria-label={`Remove product ${index + 1}`}
            className="text-[var(--mute)] hover:text-destructive"
            data-testid={`request-remove-product-${index}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {!manualMode ? (
        <div className="flex flex-col gap-2">
          {pickedVariantId ? (
            <div className="flex items-center gap-2 border border-[var(--line-warm)] bg-[var(--surface-page)] px-3 py-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-[var(--text-primary)]">{pickedTitle}</div>
                <div className="text-[var(--mute)]">
                  {pickedVariantTitle || '—'}
                  {pickedSku ? ` · SKU ${pickedSku}` : ''}
                </div>
              </div>
              <span
                className={cn(
                  'px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em]',
                  Number(pickedOnHand || 0) > 0
                    ? 'bg-[var(--status-approved-bg)] text-[var(--status-approved-fg)]'
                    : 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]'
                )}
              >
                {Number(pickedOnHand || 0) > 0 ? `In stock · ${pickedOnHand}` : 'Out of stock'}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                onClick={clearPick}
              >
                Change
              </Button>
            </div>
          ) : (
            <ProductSearchPicker
              disabled={busy}
              onPick={(item) => {
                form.setValue(`products.${index}.variant_id`, String(item.variant_id))
                form.setValue(`products.${index}.product_id`, String(item.product_id))
                form.setValue(`products.${index}.product_title`, item.product_title)
                form.setValue(`products.${index}.variant_title`, item.variant_title ?? '')
                form.setValue(`products.${index}.sku`, item.sku ?? '')
                form.setValue(`products.${index}.on_hand`, String(item.on_hand ?? 0))
                form.clearErrors(`products.${index}.product_title`)
              }}
            />
          )}
          {titleError && <p className="text-xs text-[var(--status-rejected-fg)]">{titleError.message}</p>}
          <button
            type="button"
            onClick={switchToManual}
            disabled={busy}
            className="self-start text-xs text-[var(--brand)] hover:underline"
            data-testid={tid('request-manual-toggle', index)}
          >
            Can&apos;t find it? Enter manually
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name={`products.${index}.product_title`}
            render={({ field }) => (
              <FormItem className="gap-1">
                <FormLabel className="text-xs">Product</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={busy}
                    className={inputCls}
                    placeholder="e.g. white breeches size 36"
                    data-testid={tid('request-product-title', index)}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-5 gap-3">
            {(
              [
                ['brand', 'Brand'],
                ['product_type', 'Product Type'],
                ['size', 'Size'],
                ['color', 'Color'],
                ['material', 'Material'],
              ] as const
            ).map(([name, label]) => (
              <FormField
                key={name}
                control={form.control}
                name={`products.${index}.${name}`}
                render={({ field }) => (
                  <FormItem className="gap-1">
                    <FormLabel className="text-xs">{label}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={busy} className={inputCls} />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name={`products.${index}.approx_price`}
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Approx. price point ($)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={busy}
                      className={inputCls}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`products.${index}.reference_url`}
              render={({ field }) => (
                <FormItem className="col-span-2 gap-1">
                  <FormLabel className="text-xs">Reference URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={busy}
                      className={inputCls}
                      placeholder="https://…"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>

          {/* Reference photo — one per request, uploaded after save. */}
          <div className="flex items-start gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-[var(--text-primary)]">Photo</span>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                disabled={busy}
                onChange={(e) => onPhotoPick(index, e)}
                className="text-xs text-[var(--ink-2)] file:mr-2 file:border file:border-[var(--line-input)] file:bg-transparent file:px-2 file:py-0.5 file:text-xs"
                data-testid={tid('request-photo', index)}
              />
              <span className="text-[11px] text-[var(--mute)]">Max 10 MB</span>
            </div>
            {photo.url && !photo.remove && (
              <div className="flex items-center gap-2">
                {onViewPhoto ? (
                  <button
                    type="button"
                    onClick={onViewPhoto}
                    aria-label="View photo full size"
                    title="View full size"
                    className="cursor-zoom-in"
                  >
                    <img
                      src={photo.url}
                      alt="Reference"
                      className="h-16 w-16 border border-[var(--line-warm)] object-cover"
                    />
                  </button>
                ) : (
                  <img
                    src={photo.url}
                    alt="Reference"
                    className="h-16 w-16 border border-[var(--line-warm)] object-cover"
                  />
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => onPhotoRemove(index)}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={switchToSearch}
            disabled={busy}
            className="self-start text-xs text-[var(--brand)] hover:underline"
          >
            ← Search Shopify products instead
          </button>
        </div>
      )}

      <div className="grid grid-cols-4 gap-3">
        <FormField
          control={form.control}
          name={`products.${index}.qty`}
          render={({ field }) => (
            <FormItem className="gap-1">
              <FormLabel className="text-xs">Qty</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min={1}
                  disabled={busy}
                  className={inputCls}
                  data-testid={tid('request-qty', index)}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
      </div>

      {/* ── New Products Only ──────────────────────────────── */}
      {manualMode && (
        <div>
          <SectionHeader>New Products Only</SectionHeader>
          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name={`products.${index}.vendor`}
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Vendor / source</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={busy} className={inputCls} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`products.${index}.est_cost`}
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Estimated cost ($)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={busy}
                      className={inputCls}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`products.${index}.buying_decision`}
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Buying decision</FormLabel>
                  <FormControl>
                    <select {...field} disabled={busy} className={selectCls}>
                      <option value="">— none —</option>
                      {BUYING_DECISIONS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export function LogRequestModal({
  open,
  onOpenChange,
  editRequest,
  presets,
}: LogRequestModalProps) {
  const editing = !!editRequest
  const createBatch = useCreateRequestBatch()
  const update = useUpdateRequest()
  const changeStatus = useChangeRequestStatus()
  const { data: eventLocations = [] } = useEventLocations()

  const [saving, setSaving] = React.useState(false)
  const busy =
    saving || createBatch.isPending || update.isPending || changeStatus.isPending

  // Shopify customer match panel — the row the user picked from the typeahead.
  // Editing any customer field afterwards drops it, along with the stored ids.
  const [match, setMatch] = React.useState<ShopifyCustomerInfo | null>(null)

  // Reference photos — one slot per product entry, kept index-aligned with the
  // field array. `url` is an object URL: either the newly chosen file or the
  // stored photo pulled through the authed blob endpoint (edit mode).
  const [photos, setPhotos] = React.useState<PhotoSlot[]>([{ ...EMPTY_SLOT }])
  const photoInputRefs = React.useRef<(HTMLInputElement | null)[]>([])
  const [lightboxOpen, setLightboxOpen] = React.useState(false)

  // Email 3 confirm: the values are parked here while the user answers
  // "send the your-item-is-here email?".
  const [pendingValues, setPendingValues] = React.useState<LogRequestValues | null>(null)

  const form = useForm<LogRequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: EMPTY,
  })
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'products',
  })

  const originatedFrom = form.watch('originated_from')

  // Unmount cleanup needs the *current* slots, and the cleanup only runs once.
  const photosRef = React.useRef(photos)
  React.useEffect(() => {
    photosRef.current = photos
  }, [photos])
  React.useEffect(
    () => () => {
      for (const p of photosRef.current) if (p.url) URL.revokeObjectURL(p.url)
    },
    []
  )

  React.useEffect(() => {
    if (!open) return
    for (const p of photosRef.current) if (p.url) URL.revokeObjectURL(p.url)
    setPhotos([{ ...EMPTY_SLOT }])
    setPendingValues(null)
    createBatch.reset()
    update.reset()
    changeStatus.reset()

    if (editRequest) {
      const manual = editRequest.variant_id == null
      form.reset({
        ...EMPTY,
        customer_name: editRequest.customer_name ?? '',
        customer_email: editRequest.customer_email ?? '',
        customer_phone: editRequest.customer_phone ?? '',
        shopify_customer_id: editRequest.shopify_customer_id ?? '',
        shopify_customer_url: editRequest.shopify_customer_url ?? '',
        products: [
          {
            ...EMPTY_ENTRY,
            manual_mode: manual,
            variant_id: editRequest.variant_id != null ? String(editRequest.variant_id) : '',
            product_id: editRequest.product_id != null ? String(editRequest.product_id) : '',
            product_title: editRequest.product_title ?? '',
            variant_title: editRequest.variant_title ?? '',
            sku: editRequest.sku ?? '',
            on_hand: editRequest.on_hand != null ? String(editRequest.on_hand) : '',
            brand: editRequest.brand ?? '',
            product_type: editRequest.product_type ?? '',
            size: editRequest.size ?? '',
            color: editRequest.color ?? '',
            material: editRequest.material ?? '',
            approx_price:
              editRequest.approx_price != null ? String(editRequest.approx_price) : '',
            reference_url: editRequest.reference_url ?? '',
            qty: String(editRequest.qty ?? 1),
            vendor: editRequest.vendor ?? '',
            est_cost: editRequest.est_cost != null ? String(editRequest.est_cost) : '',
            buying_decision: editRequest.buying_decision ?? '',
          },
        ],
        needed_by: editRequest.needed_by ?? '',
        originated_from: editRequest.originated_from,
        event_location_id:
          editRequest.event_location_id != null ? String(editRequest.event_location_id) : '',
        event_location_name: editRequest.event_location_name ?? '',
        urgency: editRequest.urgency,
        assigned_to: editRequest.assigned_to ?? '',
        follow_up_date: editRequest.follow_up_date ?? '',
        notes: editRequest.notes ?? '',
        status: editRequest.status,
      })
      setMatch(null)
    } else {
      form.reset({
        ...EMPTY,
        products: [{ ...EMPTY_ENTRY }],
        customer_name: presets?.customer_name ?? '',
        originated_from: presets?.originated_from ?? '',
        event_location_id:
          presets?.event_location_id != null ? String(presets.event_location_id) : '',
        event_location_name: presets?.event_location_name ?? '',
        follow_up_date: localDateIso(7),
      })
      setMatch(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editRequest])

  // Existing stored photo: pull it through the authed endpoint so the preview
  // works the same as a freshly chosen file. Edit mode is always one entry.
  React.useEffect(() => {
    if (!open || !editRequest?.has_photo) return
    let cancelled = false
    let created: string | null = null
    fetchRequestPhotoUrl(editRequest.id)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        created = url
        setPhotos((prev) => prev.map((p, i) => (i === 0 ? { ...p, url } : p)))
      })
      .catch(() => {
        /* a missing/purged photo just means no preview */
      })
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [open, editRequest?.id, editRequest?.has_photo])

  /** The picked row fills all three fields at once, so nothing else has to be
   *  looked up to draw the match panel. */
  function pickCustomer(customer: ShopifyCustomerInfo) {
    form.setValue('customer_name', customer.display_name || customer.email)
    form.setValue('customer_email', customer.email)
    form.setValue('customer_phone', customer.phone ?? '')
    form.setValue('shopify_customer_id', customer.shopify_customer_id)
    form.setValue('shopify_customer_url', customer.admin_url)
    form.clearErrors('customer_name')
    setMatch(customer)
  }

  /** Any hand-edit to a customer field means the request is no longer about
   *  the picked Shopify record — drop the link rather than save a wrong one. */
  function clearCustomerMatch() {
    if (!match) return
    setMatch(null)
    form.setValue('shopify_customer_id', '')
    form.setValue('shopify_customer_url', '')
  }

  function handlePhotoPick(index: number, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > PHOTO_MAX_BYTES) {
      toast.error('Photo is larger than 10 MB — take a smaller one or crop it')
      e.target.value = ''
      return
    }
    const prev = photos[index]?.url
    if (prev) URL.revokeObjectURL(prev)
    const url = URL.createObjectURL(file)
    setPhotos((slots) => slots.map((p, i) => (i === index ? { file, url, remove: false } : p)))
  }

  function handlePhotoRemove(index: number) {
    const prev = photos[index]?.url
    if (prev) URL.revokeObjectURL(prev)
    setPhotos((slots) =>
      slots.map((p, i) => (i === index ? { file: null, url: null, remove: true } : p))
    )
    const input = photoInputRefs.current[index]
    if (input) input.value = ''
  }

  function addProduct() {
    append({ ...EMPTY_ENTRY })
    setPhotos((slots) => [...slots, { ...EMPTY_SLOT }])
  }

  function removeProduct(index: number) {
    const prev = photos[index]?.url
    if (prev) URL.revokeObjectURL(prev)
    remove(index)
    setPhotos((slots) => slots.filter((_, i) => i !== index))
    photoInputRefs.current.splice(index, 1)
  }

  /** Edit mode: the single entry's photo, against the request being edited. */
  async function syncEditPhoto(requestId: number) {
    const slot = photos[0] ?? EMPTY_SLOT
    try {
      if (slot.file) {
        await uploadRequestPhoto(requestId, slot.file)
      } else if (slot.remove && editRequest?.has_photo) {
        await deleteRequestPhoto(requestId)
      }
    } catch (err) {
      // The request itself saved — say so, don't roll it back.
      toast.error(
        err instanceof Error ? `Request saved, photo failed: ${err.message}` : 'Photo upload failed'
      )
    }
  }

  /** Create mode: the batch came back in submit order, so slot i belongs to
   *  request i. A failed upload is reported, never rolled back. */
  async function uploadStagedPhotos(created: ProductRequest[]) {
    for (let i = 0; i < created.length; i++) {
      const file = photos[i]?.file
      if (!file) continue
      try {
        await uploadRequestPhoto(created[i].id, file)
      } catch (err) {
        toast.error(
          err instanceof Error
            ? `Request saved, photo failed: ${err.message}`
            : 'Photo upload failed'
        )
      }
    }
  }

  async function persist(values: LogRequestValues, sendEmail: boolean) {
    const shared = buildShared(values)
    setSaving(true)
    try {
      if (editing && editRequest) {
        await update.mutateAsync({
          id: editRequest.id,
          patch: { ...shared, ...buildProduct(values.products[0]) },
        })
        await syncEditPhoto(editRequest.id)
        // Status is owned by its own endpoint (it also stamps resolved_at and
        // the email), so it moves after the PATCH and only when it changed.
        if (values.status !== editRequest.status) {
          await changeStatus.mutateAsync({
            id: editRequest.id,
            status: values.status,
            send_email: sendEmail,
          })
        }
        toast.success('Request updated')
      } else {
        const payload: ProductRequestBatchPayload = {
          ...shared,
          customer_name: values.customer_name.trim(),
          originated_from: values.originated_from as RequestOrigin,
          status: values.status,
          products: values.products.map(buildProduct),
        }
        const res = await createBatch.mutateAsync(payload)
        await uploadStagedPhotos(res.requests)
        toast.success(
          res.requests.length === 1
            ? 'Request logged'
            : `${res.requests.length} requests logged`
        )
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save request')
    } finally {
      setSaving(false)
      setPendingValues(null)
    }
  }

  async function onSubmit(values: LogRequestValues) {
    // Email 3: moving a request to Customer Notified is the one transition
    // that can email the customer, so ask before it happens. Skipped when
    // there's no address to send to.
    const notifying =
      editing &&
      editRequest != null &&
      values.status === 'CUSTOMER_NOTIFIED' &&
      values.status !== editRequest.status &&
      values.customer_email.trim() !== ''
    if (notifying) {
      setPendingValues(values)
      return
    }
    await persist(values, false)
  }

  const confirmEmail = pendingValues?.customer_email.trim() ?? ''

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex max-h-[88vh] w-[92vw] max-w-[760px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[760px]"
        showCloseButton={false}
      >
        <DialogHeader className="border-b border-[var(--line-warm)] px-5 py-3 sm:text-left">
          <DialogTitle className="text-base font-semibold">
            {editing ? 'Edit request' : 'Log product request'}
          </DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            {editing
              ? 'Update the customer, the product, or where this request is in the pipeline.'
              : 'Record something a customer asked for that we did not have.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
              {/* ── Customer Information ───────────────────────────── */}
              <section>
                <SectionHeader>Customer Information</SectionHeader>
                <div className="grid grid-cols-3 gap-3">
                  <FormField
                    control={form.control}
                    name="customer_name"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Customer name</FormLabel>
                        <FormControl>
                          <CustomerSearchField
                            field={field}
                            autoFocus
                            disabled={busy}
                            testid="request-customer-name"
                            onEdit={clearCustomerMatch}
                            onPick={pickCustomer}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="customer_email"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Email</FormLabel>
                        <FormControl>
                          <CustomerSearchField
                            field={field}
                            type="email"
                            disabled={busy}
                            testid="request-customer-email"
                            onEdit={clearCustomerMatch}
                            onPick={pickCustomer}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="customer_phone"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Phone</FormLabel>
                        <FormControl>
                          <CustomerSearchField
                            field={field}
                            disabled={busy}
                            testid="request-customer-phone"
                            onEdit={clearCustomerMatch}
                            onPick={pickCustomer}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                {match && (
                  <div className="mt-2 border border-[var(--line-warm)] bg-[var(--surface-page)] px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[var(--text-primary)]">
                        {match.display_name || match.email}
                      </span>
                      <span className="text-[var(--mute)]">
                        {match.orders_count} {match.orders_count === 1 ? 'order' : 'orders'} ·{' '}
                        {fmtMoney(match.total_spent)} total
                        {match.last_order_at
                          ? ` · last order ${fmtDate(match.last_order_at)}`
                          : ''}
                      </span>
                      <div className="flex-1" />
                      <a
                        href={match.admin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[var(--brand)] hover:underline"
                      >
                        View in Shopify
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                )}
              </section>

              {/* ── Product(s) ─────────────────────────────────────── */}
              <section>
                <SectionHeader>{editing ? 'Product' : 'Products'}</SectionHeader>
                <div className="flex flex-col gap-3">
                  {fields.map((f, index) => (
                    <ProductEntryCard
                      key={f.id}
                      form={form}
                      index={index}
                      busy={busy}
                      photo={photos[index] ?? EMPTY_SLOT}
                      onPhotoPick={handlePhotoPick}
                      onPhotoRemove={handlePhotoRemove}
                      photoInputRef={(el) => {
                        photoInputRefs.current[index] = el
                      }}
                      // Full size + download only make sense against the stored
                      // photo; once a new file is staged the preview is local.
                      onViewPhoto={
                        editRequest?.has_photo && index === 0 && !photos[0]?.file
                          ? () => setLightboxOpen(true)
                          : undefined
                      }
                      // The first entry is the request itself, and edit mode is
                      // single-product by design — neither can be removed.
                      onRemove={
                        !editing && index > 0 ? () => removeProduct(index) : undefined
                      }
                    />
                  ))}
                  {!editing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 self-start text-xs"
                      disabled={busy}
                      onClick={addProduct}
                      data-testid="request-add-product"
                    >
                      + Add another product
                    </Button>
                  )}
                </div>
              </section>

              {/* ── Needed By ──────────────────────────────────────── */}
              <section>
                <div className="grid grid-cols-4 gap-3">
                  <FormField
                    control={form.control}
                    name="needed_by"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">
                          Needed by{' '}
                          <span className="font-normal text-[var(--mute)]">(optional)</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} type="date" disabled={busy} className={inputCls} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              </section>

              {/* ── Request Context ────────────────────────────────── */}
              <section>
                <SectionHeader>Request Context</SectionHeader>
                <div className="grid grid-cols-3 gap-3">
                  <FormField
                    control={form.control}
                    name="originated_from"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Originated from</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            disabled={busy}
                            className={selectCls}
                            data-testid="request-origin"
                          >
                            <option value="">— select —</option>
                            {ORIGINS.map((o) => (
                              <option key={o} value={o}>
                                {ORIGIN_LABELS[o]}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  {originatedFrom === 'EVENT' && (
                    <FormField
                      control={form.control}
                      name="event_location_id"
                      render={({ field }) => (
                        <FormItem className="gap-1">
                          <FormLabel className="text-xs">Event</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              disabled={busy}
                              className={selectCls}
                              data-testid="request-event"
                              onChange={(e) => {
                                field.onChange(e)
                                const loc = eventLocations.find(
                                  (l) => String(l.location_id) === e.target.value
                                )
                                form.setValue('event_location_name', loc?.name ?? '')
                              }}
                            >
                              <option value="">— select event —</option>
                              {eventLocations.map((l) => (
                                <option key={l.location_id} value={String(l.location_id)}>
                                  {l.name}
                                </option>
                              ))}
                            </select>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="urgency"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Urgency</FormLabel>
                        <FormControl>
                          <select {...field} disabled={busy} className={selectCls}>
                            <option value="STANDARD">Standard</option>
                            <option value="URGENT">Urgent</option>
                          </select>
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              </section>

              {/* ── Assignment ─────────────────────────────────────── */}
              <section>
                <SectionHeader>Assignment</SectionHeader>
                <div className="grid grid-cols-3 gap-3">
                  <FormField
                    control={form.control}
                    name="assigned_to"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Assigned to</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            disabled={busy}
                            className={selectCls}
                            data-testid="request-assignee"
                          >
                            <option value="">— unassigned —</option>
                            {ASSIGNEES.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="follow_up_date"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Follow up by</FormLabel>
                        <FormControl>
                          <Input {...field} type="date" disabled={busy} className={inputCls} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem className="gap-1">
                        <FormLabel className="text-xs">Status</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            disabled={busy}
                            className={selectCls}
                            data-testid="request-status"
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {REQUEST_STATUS_LABELS[s]}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              </section>

              {/* ── Notes ──────────────────────────────────────────── */}
              <section>
                <SectionHeader>Notes</SectionHeader>
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <textarea
                          {...field}
                          rows={3}
                          disabled={busy}
                          placeholder="Anything else worth remembering…"
                          className="w-full border border-[var(--line-input)] bg-transparent px-2 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand)]"
                          data-testid="request-notes"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </section>
            </div>

            {/* ── Footer / Email 3 confirm ───────────────────────────── */}
            {pendingValues ? (
              <div className="flex items-center gap-2 border-t border-[var(--line-warm)] bg-[var(--surface-page)] px-5 py-3">
                <span className="text-xs text-[var(--text-primary)]">
                  Send the &ldquo;your item is here&rdquo; email to {confirmEmail}?
                </span>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => setPendingValues(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => void persist(pendingValues, false)}
                  data-testid="request-no-email"
                >
                  Don&apos;t send
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => void persist(pendingValues, true)}
                  data-testid="request-send-email"
                >
                  Send email
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2 border-t border-[var(--line-warm)] px-5 py-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  data-testid="request-submit"
                >
                  {busy ? 'Saving…' : editing ? 'Save Request' : 'Log Request'}
                </Button>
              </div>
            )}
          </form>
        </Form>

        {editRequest && (
          <PhotoLightbox
            requestId={editRequest.id}
            open={open && lightboxOpen}
            onOpenChange={setLightboxOpen}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
